import { Fragment } from 'react';
import { Row } from '../Row';
import { Box } from '../Box';
import { Item } from './Item';
import { Colors } from '~/themes/colors';

interface IStepperProps {
  /** Array of step titles to display */
  steps: string[];
  /**
   * The current active step index (0-based).
   * Steps before this index will be marked as completed.
   * Defaults to 0.
   * @default 0
   */
  currentStep?: number;
}

/**
 * A horizontal stepper component that displays a series of steps with visual indicators.
 *
 * @example
 * ```tsx
 * <Stepper
 *   steps={['Personal Info', 'Payment', 'Confirmation']}
 *   currentStep={1}
 * />
 * ```
 *
 * @param props - The stepper component props
 * @returns A horizontal stepper with step indicators and connecting lines
 */
export const Stepper = ({ steps, currentStep = 0 }: IStepperProps) => {
  if (process.env.NODE_ENV !== 'production') {
    if (currentStep < 0) {
      console.warn(`Stepper: currentStep (${currentStep}) is negative`);
    }
    if (currentStep >= steps.length) {
      console.warn(`Stepper: currentStep (${currentStep}) exceeds steps length (${steps.length})`);
    }
  }

  return (
    <Row alignItems="center" justifyContent="space-between" width="100%">
      {steps.map((step, index) => {
        const isCompleteStep = index < currentStep;
        const isCurrentStep = index === currentStep;

        return (
          <Fragment key={step}>
            <Item title={step} isActive={isCurrentStep} isCompleted={isCompleteStep} />
            {index < steps.length - 1 && (
              <Box
                data-testid="steps.separator"
                backgroundColor={isCompleteStep ? Colors.success_400 : Colors.neutral_200}
                flex={1}
                height={2}
                marginLeft={8}
              />
            )}
          </Fragment>
        );
      })}
    </Row>
  );
};
